import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { BookingSelectionConfig, TableItem, TableSelection } from './booking-selection.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-booking-selection',
  templateUrl: './booking-selection.component.html',
  styleUrls: ['./booking-selection.component.scss']
})
export class BookingSelectionComponent implements OnChanges {
  @Input() loading: boolean = false;
  @Input() config: BookingSelectionConfig = null;
  @Input() shift: any;
  @Output() selectionChange = new EventEmitter<TableSelection[]>();
  constructor(private translateService: TranslateService) {}
  tables: TableItem[] = [];
  allTimes: string[] = [];
  slotsNeeded: number = 3;

  availableTimeMap: { [tableId: number]: { [time: string]: boolean } } = {};

  stayTime: number = 60;
  pricePerBooking: number = 0;
  currencySymbol: string = '€';
  maxSelections: number = 4;

  selectedMap: { [tableId: number]: { [startTime: string]: boolean } } = {};
  selections: TableSelection[] = [];
  lockedStartTime: string = null;

  hoveredTableId: number = null;
  hoveredBlockStart: string = null;

  errorMsg: string = null;
  private errorTimer: any = null;
  timeIndexMap: Record<string, number> = {};
  tableLabel: string;

  // ── Lifecycle ──────────────────────────────────────────────────
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      const prev = changes['config'].previousValue;
      const curr = changes['config'].currentValue;
      if (JSON.stringify(prev) !== JSON.stringify(curr)) {
        this.applyConfig();
      }
    }
  }

  // ── Apply config ───────────────────────────────────────────────
  private applyConfig(): void {
    if (!this.config || !this.config.tables || !this.config.allTimes) {
      this.tables = [];
      this.allTimes = [];
      this.availableTimeMap = {};
      this.clearAll();
      return;
    }

    this.tables = [...this.config.tables];
    if (this.tables && this.tables.length) {
      this.tables = this.tables.filter(t => !t.isCombined);
    }

    this.allTimes = [...this.config.allTimes];

    this.tableLabel =
      this.shift && this.shift.tableLabel
        ? this.maxSelections > 1
          ? this.shift.tableLabel.amountLabelPlural
          : this.shift.tableLabel.amountLabelSingle
        : this.translateService.instant('tablePlan.tables');

    this.timeIndexMap = {};
    this.allTimes.forEach((t, i) => (this.timeIndexMap[t] = i));

    this.stayTime = this.config.stayTime || 60;
    this.pricePerBooking = this.config.pricePerBooking || 0;
    this.currencySymbol = this.config.currencySymbol || '€';
    this.maxSelections = this.config.maxSelections || 4;
    this.slotsNeeded = Math.ceil(this.stayTime / this.config.interval);

    this.availableTimeMap = {};

    const matrix = this.config.slotMatrix || [];

    for (let i = 0; i < matrix.length; i++) {
      const slot = matrix[i];
      const time = slot.time;
      const slotTables = slot.tables || [];

      for (let j = 0; j < slotTables.length; j++) {
        const tableId = slotTables[j].id;

        if (!this.availableTimeMap[tableId]) {
          this.availableTimeMap[tableId] = {};
        }

        this.availableTimeMap[tableId][time] = true;
      }
    }
    this.clearAll();
  }

  // ── Error helper ───────────────────────────────────────────────
  private showError(msg: string): void {
    this.errorMsg = msg;
    if (this.errorTimer) {
      clearTimeout(this.errorTimer);
    }
    this.errorTimer = setTimeout(() => {
      this.errorMsg = null;
    }, 4000);
  }

  dismissError(): void {
    this.errorMsg = null;
    if (this.errorTimer) {
      clearTimeout(this.errorTimer);
      this.errorTimer = null;
    }
  }

  // ── Slot index helpers ─────────────────────────────────────────
  private indexOfTime(time: string): number {
    const index = this.timeIndexMap[time];
    return index !== undefined ? index : -1;
  }

  private getBlockStartForRow(tableId: number, rowTime: string): string {
    const tableMap = this.selectedMap[tableId];
    if (!tableMap) return null;
    const rowIndex = this.indexOfTime(rowTime);
    const slotsNeeded = this.getSlotsNeededForTable(tableId);
    for (const start of Object.keys(tableMap)) {
      const startIndex = this.indexOfTime(start);
      if (rowIndex >= startIndex && rowIndex < startIndex + slotsNeeded) {
        return start;
      }
    }
    return null;
  }

  isInSelectedBlock(tableId: number, rowTime: string): boolean {
    return this.getBlockStartForRow(tableId, rowTime) !== null;
  }

  isBlockStart(tableId: number, rowTime: string): boolean {
    const tableMap = this.selectedMap[tableId];
    if (!tableMap) {
      return false;
    }
    return !!tableMap[rowTime];
  }

  isBlockEnd(tableId: number, rowTime: string): boolean {
    const tableMap = this.selectedMap[tableId];
    if (!tableMap) return false;
    const rowIndex = this.indexOfTime(rowTime);
    const slotsNeeded = this.getSlotsNeededForTable(tableId);
    const starts = Object.keys(tableMap);
    for (let i = 0; i < starts.length; i++) {
      const start = starts[i];
      if (!tableMap[start]) continue;
      const endIndex = this.indexOfTime(start) + slotsNeeded - 1;
      if (rowIndex === endIndex) return true;
    }
    return false;
  }

  // ── Hover helpers ──────────────────────────────────────────────
  isInHoverBlock(tableId: number, rowTime: string): boolean {
    if (this.hoveredTableId !== tableId || !this.hoveredBlockStart) {
      return false;
    }
    const startIndex = this.indexOfTime(this.hoveredBlockStart);
    const rowIndex = this.indexOfTime(rowTime);
    const slotsNeeded = this.getSlotsNeededForTable(tableId);
    return rowIndex >= startIndex && rowIndex < startIndex + slotsNeeded;
  }

  isHoverBlockStart(tableId: number, rowTime: string): boolean {
    return this.hoveredTableId === tableId && this.hoveredBlockStart === rowTime;
  }

  isHoverBlockEnd(tableId: number, rowTime: string): boolean {
    if (this.hoveredTableId !== tableId || !this.hoveredBlockStart) return false;
    const startIndex = this.indexOfTime(this.hoveredBlockStart);
    const rowIndex = this.indexOfTime(rowTime);
    const slotsNeeded = this.getSlotsNeededForTable(tableId);
    return rowIndex === startIndex + slotsNeeded - 1;
  }

  // The booking's locked-in real start time — derived straight from the
  // first current selection, since all selections are guaranteed to share
  // one real start time by construction (see handleCellClick).
  private getLockedActualStartTime(): string | null {
    return this.selections.length ? this.selections[0].startTime : null;
  }

  // Public wrapper so the template can resolve a row's block start without
  // needing its own copy of the (private) lookup logic.
  getSelectedBlockStartRow(tableId: number, rowTime: string): string | null {
    return this.getBlockStartForRow(tableId, rowTime);
  }

  // Which row is at the visual/temporal CENTER of the block — this is
  // where the time label should render, instead of always on the first
  // row. On an offset table the first row is only partially colored, so
  // centering the label there (against the full row height) puts the text
  // right on top of the border instead of inside the colored area.
  private isCenterRow(actualStartMinutes: number, rowTime: string): boolean {
    const interval = this.config.interval;
    const centerMinute = actualStartMinutes + this.stayTime / 2;
    const rowStart = this.toMinutes(rowTime);
    const rowEnd = rowStart + interval;
    return centerMinute >= rowStart && centerMinute < rowEnd;
  }

  isLabelRow(tableId: number, rowTime: string): boolean {
    const blockStart = this.getBlockStartForRow(tableId, rowTime);
    if (!blockStart) return false;
    const actualStartMinutes = this.toMinutes(this.getActualStartTime(tableId, blockStart));
    return this.isCenterRow(actualStartMinutes, rowTime);
  }

  isHoverLabelRow(tableId: number, rowTime: string): boolean {
    if (this.hoveredTableId !== tableId || !this.hoveredBlockStart) return false;
    if (!this.isInHoverBlock(tableId, rowTime)) return false;
    const actualStartMinutes = this.toMinutes(this.getActualStartTime(tableId, this.hoveredBlockStart));
    return this.isCenterRow(actualStartMinutes, rowTime);
  }

  // Exact fractional position (0..1) of the block's temporal center within
  // THIS row. Used with translateY(-50%) so the label's own center lands
  // precisely at the block's true center — including the case where the
  // center falls exactly on a row boundary (even number of rows), which a
  // "pick one row, center within its full height" approach can't handle
  // without an arbitrary, visually-biased tie-break.
  private getBlockCenterFraction(actualStartMinutes: number, rowTime: string): number {
    const interval = this.config.interval;
    const centerMinute = actualStartMinutes + this.stayTime / 2;
    const rowStart = this.toMinutes(rowTime);
    return (centerMinute - rowStart) / interval;
  }

  getLabelStyle(tableId: number, rowTime: string): { [key: string]: string } {
    const blockStart = this.getBlockStartForRow(tableId, rowTime);
    if (!blockStart) return {};
    const actualStartMinutes = this.toMinutes(this.getActualStartTime(tableId, blockStart));
    const fraction = this.getBlockCenterFraction(actualStartMinutes, rowTime);
    return { top: `${fraction * 100}%` };
  }

  getHoverLabelStyle(tableId: number, rowTime: string): { [key: string]: string } {
    if (this.hoveredTableId !== tableId || !this.hoveredBlockStart) return {};
    const actualStartMinutes = this.toMinutes(this.getActualStartTime(tableId, this.hoveredBlockStart));
    const fraction = this.getBlockCenterFraction(actualStartMinutes, rowTime);
    return { top: `${fraction * 100}%` };
  }

  isHoverBlockValid(tableId: number): boolean {
    if (!this.hoveredBlockStart) return false;
    const startIndex = this.indexOfTime(this.hoveredBlockStart);
    if (startIndex < 0) return false;

    const slotsNeeded = this.getSlotsNeededForTable(tableId);

    // Boundary: block must not spill past the end of the loaded grid
    if (startIndex + slotsNeeded > this.allTimes.length) {
      return false;
    }

    // Must match the booking's real locked start time, not just the row.
    // A 0-offset table clicked at row 16:00 (real 16:00) and a 15-offset
    // table clicked at row 16:00 (real 16:15) are NOT the same booking.
    const lockedActualStart = this.getLockedActualStartTime();
    if (lockedActualStart) {
      const candidateActualStart = this.getActualStartTime(tableId, this.hoveredBlockStart);
      if (candidateActualStart !== lockedActualStart) {
        return false;
      }
    }

    for (let i = 0; i < slotsNeeded; i++) {
      const t = this.allTimes[startIndex + i];
      if (!(this.availableTimeMap[tableId] && this.availableTimeMap[tableId][t])) {
        return false;
      }
      if (this.isInSelectedBlock(tableId, t)) {
        return false;
      }
    }
    return true;
  }

  // ── CSS classes ────────────────────────────────────────────────
  getCellClasses(tableId: number, rowTime: string): { [cls: string]: boolean } {
    const available = !!(this.availableTimeMap[tableId] && this.availableTimeMap[tableId][rowTime]);
    const inSelected = this.isInSelectedBlock(tableId, rowTime);
    const isStart = this.isBlockStart(tableId, rowTime);
    const isEnd = this.isBlockEnd(tableId, rowTime);
    const inHover = this.isInHoverBlock(tableId, rowTime);
    const hoverValid = inHover && this.isHoverBlockValid(tableId);
    const isHoverStart = this.isHoverBlockStart(tableId, rowTime);
    const timeLocked = !!this.getLockedActualStartTime() && !hoverValid;

    return {
      'slot-td-booked': !available && !inSelected,
      'slot-td-selected': inSelected,
      // Suppress the plain grid border wherever two selected rows of the
      // SAME block sit back-to-back, so a multi-row block reads as one
      // seamless green area instead of having a gray line through it.
      'slot-td-suppress-border-top': inSelected && !isStart,
      'slot-td-suppress-border-bottom': inSelected && !isEnd,
      'slot-td-hover': inHover && hoverValid && !inSelected,
      'slot-td-hover-start': isHoverStart && hoverValid && !inSelected,
      'slot-td-hover-no': inHover && (!hoverValid || timeLocked) && !inSelected,
      'slot-td-locked':
        available &&
        !inSelected &&
        !inHover &&
        !!this.lockedStartTime &&
        this.indexOfTime(rowTime) !== this.indexOfTime(this.lockedStartTime),
      'slot-td-default': available && !inSelected && !inHover && !this.lockedStartTime
    };
  }

  getTitle(tableId: number, tableName: string, rowTime: string): string {
    if (!(this.availableTimeMap[tableId] && this.availableTimeMap[tableId][rowTime])) {
      return tableName + ' – ' + this.translateService.instant('widget.reservation.booking.notAvailable');
    }
    const blockStart = this.getBlockStartForRow(tableId, rowTime);
    if (blockStart) {
      return (
        tableName +
        ' ' +
        this.formatTime(this.getActualStartTime(tableId, blockStart)) +
        ' – ' +
        this.formatTime(this.calcEndTime(blockStart, tableId)) +
        ' – ' +
        this.translateService.instant('widget.reservation.booking.clickToRemove')
      );
    }
    return (
      tableName +
      ' – ' +
      this.translateService.instant('widget.reservation.booking.clickToBookFrom', {
        time: this.formatTime(this.getActualStartTime(tableId, rowTime))
      })
    );
  }

  // ── Click handler ──────────────────────────────────────────────
  handleCellClick(tableId: number, tableName: string, rowTime: string): void {
    if (!(this.availableTimeMap[tableId] && this.availableTimeMap[tableId][rowTime])) {
      return;
    }

    // Remove existing block if clicked (match on rowKey, not display startTime!)
    const existingStart = this.getBlockStartForRow(tableId, rowTime);
    if (existingStart) {
      delete this.selectedMap[tableId][existingStart];
      this.selections = this.selections.filter(s => !(s.tableId === tableId && s.rowKey === existingStart));
      if (this.selections.length === 0) {
        this.lockedStartTime = null;
      }
      this.selectionChange.emit(this.selections.slice());
      return;
    }

    // The booking's real start time must match across every selected table.
    // A 0-offset table's real start (=rowTime) and a 15-offset table's real
    // start (=rowTime+15) are different bookings even if the clicked row
    // happens to be the same — so compare ACTUAL start times, not rows.
    const candidateActualStart = this.getActualStartTime(tableId, rowTime);
    const lockedActualStart = this.getLockedActualStartTime();
    if (lockedActualStart && candidateActualStart !== lockedActualStart) {
      this.showError(
        this.translateService.instant('widget.reservation.booking.alreadySelectedTime', {
          time: this.formatTime(lockedActualStart)
        })
      );
      return;
    }

    // Validate block slots
    const slotsNeeded = this.getSlotsNeededForTable(tableId);
    const startIndex = this.indexOfTime(rowTime);

    if (startIndex + slotsNeeded > this.allTimes.length) {
      this.showError(
        this.translateService.instant('widget.reservation.booking.notEnoughSlots', {
          minutes: this.stayTime
        })
      );
      return;
    }

    for (let i = 0; i < slotsNeeded; i++) {
      const t = this.allTimes[startIndex + i];
      if (!(this.availableTimeMap[tableId] && this.availableTimeMap[tableId][t])) {
        this.showError(
          this.translateService.instant('widget.reservation.booking.requiredSlotsUnavailable', {
            time: this.formatTime(rowTime)
          })
        );
        return;
      }
      if (this.isInSelectedBlock(tableId, t)) {
        return;
      }
    }

    // Max tables guard
    if (this.selections.length >= this.maxSelections) {
      this.showError(
        this.translateService.instant('widget.reservation.booking.maxTablesReached', {
          max: this.maxSelections,
          tableLabel: this.tableLabel
        })
      );
      return;
    }

    if (!this.selectedMap[tableId]) {
      this.selectedMap[tableId] = {};
    }
    this.selectedMap[tableId][rowTime] = true;
    this.lockedStartTime = rowTime;

    this.selections = this.selections.concat([
      {
        tableId: tableId,
        tableName: tableName,
        startTime: candidateActualStart,
        endTime: this.calcEndTime(rowTime, tableId),
        rowKey: rowTime
      }
    ]);
    this.selectionChange.emit(this.selections.slice());
  }

  // ── Mouse events ───────────────────────────────────────────────
  onMouseEnter(tableId: number, rowTime: string): void {
    if (!(this.availableTimeMap[tableId] && this.availableTimeMap[tableId][rowTime])) {
      return;
    }
    const startIndex = this.indexOfTime(rowTime);
    const slotsNeeded = this.getSlotsNeededForTable(tableId);
    if (startIndex + slotsNeeded > this.allTimes.length) {
      return;
    }
    this.hoveredTableId = tableId;
    this.hoveredBlockStart = rowTime;
  }

  onMouseLeave(): void {
    this.hoveredTableId = null;
    this.hoveredBlockStart = null;
  }

  // ── Selection management ───────────────────────────────────────
  removeSelection(tableId: number, rowKey: string): void {
    if (this.selectedMap[tableId]) {
      delete this.selectedMap[tableId][rowKey];
    }
    this.selections = this.selections.filter(s => !(s.tableId === tableId && s.rowKey === rowKey));
    if (this.selections.length === 0) {
      this.lockedStartTime = null;
    }
    this.selectionChange.emit(this.selections.slice());
  }

  clearAll(): void {
    this.selectedMap = {};
    this.selections = [];
    this.lockedStartTime = null;
    this.errorMsg = null;

    if (this.errorTimer) {
      clearTimeout(this.errorTimer);
      this.errorTimer = null;
    }
    this.selectionChange.emit([]);
  }

  hasSelection(): boolean {
    return this.selections.length > 0;
  }

  // ── Time helpers ───────────────────────────────────────────────
  private toMinutes(time: string): number {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  }

  private addMinutesToTime(time: string, minutes: number): string {
    const total = this.toMinutes(time) + minutes;
    const hh = Math.floor(total / 60) % 24;
    const mm = total % 60;
    return `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
  }

  calcEndTime(startTime: string, tableId?: number): string {
    const offset = tableId != null ? this.getTableOffset(tableId) : 0;
    return this.addMinutesToTime(startTime, offset + this.stayTime);
  }

  formatTime(time: string): string {
    const parts = time.split(':');
    const h = parseInt(parts[0], 10);
    const m = parts[1];

    const hh = h.toString().padStart(2, '0');
    return `${hh}:${m}`;
  }

  formatPrice(amount: number): string {
    return amount.toFixed(2).replace('.', ',');
  }

  getGrandTotal(): string {
    return this.formatPrice(this.pricePerBooking * this.selections.length);
  }

  // private getTableOffset(tableId: number): number {
  //   const table = this.tables.find(t => t.id === tableId);
  //   // TEMPORARY TEST SHIM — remove once table.offsetMinutes is populated
  //   // from the real backend/config for tables that need a turnover offset.
  //   if (table && (table.name == '3' || table.name == '8')) {
  //     return 15;
  //   }
  //   return table && table.offsetMinutes != null ? table.offsetMinutes : 0;
  // }

  private getTableOffset(tableId: number): number {
    const table = this.tables.find(t => t.id === tableId);
    return table && table.startOffsetMinutes != null ? table.startOffsetMinutes : 0;
  }

  private getSlotsNeededForTable(tableId: number): number {
    const offset = this.getTableOffset(tableId);
    return Math.ceil((offset + this.stayTime) / this.config.interval);
  }

  getActualStartTime(tableId: number, rowTime: string): string {
    const offset = this.getTableOffset(tableId);
    if (!offset) return rowTime;
    return this.addMinutesToTime(rowTime, offset);
  }

  // ── Fill layer (colored region + border) for a selected cell ───────
  // Renders the exact overlap, in minutes, between the block's real time
  // range and this row's real time range — so the colored area and its
  // border always align to the true start/end time, never to the row edge.
  getSelectedFillStyle(tableId: number, rowTime: string): { [key: string]: string } {
    const blockStart = this.getBlockStartForRow(tableId, rowTime);
    if (!blockStart) return {};
    const isStart = this.isBlockStart(tableId, rowTime);
    const isEnd = this.isBlockEnd(tableId, rowTime);
    return this.getFillPercentages(tableId, blockStart, rowTime, isStart, isEnd);
  }

  // border-top only on the row that is the block's true first row, and
  // border-bottom only on the row that is the block's true last row — the
  // fill div's own top/bottom offset already places these at the real
  // start/end time, not at the row edge.
  getSelectedFillClasses(tableId: number, rowTime: string): { [cls: string]: boolean } {
    return {
      'bw-fill-edge-top': this.isBlockStart(tableId, rowTime),
      'bw-fill-edge-bottom': this.isBlockEnd(tableId, rowTime)
    };
  }

  getHoverFillStyle(tableId: number, rowTime: string): { [key: string]: string } {
    if (this.hoveredTableId !== tableId || !this.hoveredBlockStart) return {};
    const isStart = this.isHoverBlockStart(tableId, rowTime);
    const isEnd = this.isHoverBlockEnd(tableId, rowTime);
    return this.getFillPercentages(tableId, this.hoveredBlockStart, rowTime, isStart, isEnd);
  }

  getHoverFillClasses(tableId: number, rowTime: string): { [cls: string]: boolean } {
    return {
      'bw-fill-edge-top': this.isHoverBlockStart(tableId, rowTime),
      'bw-fill-edge-bottom': this.isHoverBlockEnd(tableId, rowTime)
    };
  }

  // Shared by both selected and hover fills.
  // isRowStart/isRowEnd tell us whether THIS row is the block's true first/
  // last row. For any row that is NOT the true edge on a given side, the
  // real overlap percentage on that side is always exactly 0% — meaning an
  // adjacent row (also part of the same block) has its own fill touching
  // this same boundary. Two independently-rendered absolutely-positioned
  // divs meeting at exactly 0%/100% can leave a hairline seam due to
  // sub-pixel rounding, so on non-edge sides we extend the fill by 1px past
  // the row boundary to overlap the neighbor instead of just touching it —
  // this never affects a real edge, since real edges only occur where
  // isRowStart/isRowEnd is true and use the exact percentage instead.
  private getFillPercentages(
    tableId: number,
    blockStartRow: string,
    rowTime: string,
    isRowStart: boolean,
    isRowEnd: boolean
  ): { [key: string]: string } {
    const interval = this.config.interval;
    const actualStartMinutes = this.toMinutes(this.getActualStartTime(tableId, blockStartRow));
    const actualEndMinutes = actualStartMinutes + this.stayTime;
    const rowStartMinutes = this.toMinutes(rowTime);
    const rowEndMinutes = rowStartMinutes + interval;

    const overlapStart = Math.max(actualStartMinutes, rowStartMinutes);
    const overlapEnd = Math.min(actualEndMinutes, rowEndMinutes);

    const topPercent = ((overlapStart - rowStartMinutes) / interval) * 100;
    const bottomPercent = ((rowEndMinutes - overlapEnd) / interval) * 100;

    return {
      top: isRowStart ? `${topPercent}%` : 'calc(0% - 1px)',
      bottom: isRowEnd ? `${bottomPercent}%` : 'calc(0% - 1px)'
    };
  }
}
